import React from "react";
import {
  AlertType,
  buildAlertClasses,
  alertSizeClasses,
} from "@/utils/alert-styles";
import { createAlertIcon } from "@/utils/alert-helpers";
import { Button } from "@/components/ui/button";

export interface BaseAlertProps {
  type: AlertType;
  title: string;
  message: string;
  icon?: React.ReactNode;
  size?: keyof typeof alertSizeClasses;
  className?: string;
  showAction?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  "data-testid"?: string;
}

export const BaseAlert: React.FC<BaseAlertProps> = ({
  type,
  title,
  message,
  icon: customIcon,
  size = "default",
  className,
  showAction = false,
  actionLabel = "Ação",
  onAction,
  "data-testid": testId,
}) => {
  const alertIcon = createAlertIcon(type, customIcon);
  const alertClasses = buildAlertClasses(type, size, className);

  return (
    <div
      className={`flex items-start gap-1 p-4 ${alertClasses}`}
      role="alert"
      aria-live="polite"
      data-testid={testId}
    >
      {alertIcon}
      <div className="flex-1 text-sm mt-[-2px]">
        <p className="font-medium text-[16px]" data-testid={`${testId}-title`}>
          {title}
        </p>
        {message && (
          <p
            className="text-xs mt-1 leading-relaxed"
            data-testid={`${testId}-message`}
          >
            {message}
          </p>
        )}
        {showAction && onAction && (
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onAction}
              className="text-sm h-7"
              data-testid={`${testId}-action`}
            >
              {actionLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

BaseAlert.displayName = "BaseAlert";
